import type { LiveblocksThread } from 'providers/CommentsContext';
import { DEFAULT_THREAD_POSITION_TOP } from '../../helpers/ThreadPositionManager';

export interface GroupedThreadPosition {
  position: number;
  threads: Array<{
    threadId: string;
    isResolved: boolean;
  }>;
}

export interface GroupedThread {
  position: number;
  threads: Array<{
    thread: LiveblocksThread;
    isResolved: boolean;
  }>;
}

export interface FilteredGroupedThread {
  position: number;
  threads: Array<{
    thread: LiveblocksThread;
    isResolved: boolean;
  }>;
}

export const groupThreadsByPosition = (groupedThreadPositions: GroupedThreadPosition[], threads: LiveblocksThread[]): GroupedThread[] => {
  const threadMap = new Map<string, LiveblocksThread>();
  threads.forEach((thread) => {
    threadMap.set(thread.id, thread);
  });

  const groups: GroupedThread[] = [];

  groupedThreadPositions.forEach(({ position, threads: positionThreads }) => {
    const threadObjects = positionThreads
      .map(({ threadId, isResolved }) => {
        const thread = threadMap.get(threadId);
        return thread ? { thread, isResolved } : null;
      })
      .filter((item): item is { thread: LiveblocksThread; isResolved: boolean } => item !== null);

    if (threadObjects.length > 0) {
      groups.push({
        position,
        threads: threadObjects,
      });
    }
  });

  groups.sort((a, b) => a.position - b.position);

  return groups;
};

export const filterGroupedThreads = (groupedThreads: GroupedThread[], threadTab: 'active' | 'resolved'): FilteredGroupedThread[] => {
  const filteredGroups: FilteredGroupedThread[] = [];

  for (const group of groupedThreads) {
    const filteredThreads = group.threads.filter(({ isResolved }) => (threadTab === 'active' ? !isResolved : isResolved));

    if (filteredThreads.length > 0) {
      filteredGroups.push({
        position: group.position,
        threads: filteredThreads,
      });
    }
  }

  return filteredGroups;
};

export const groupThreadsByBlockId = (threads: LiveblocksThread[]): GroupedThread[] => {
  const groupedMap = new Map<number, GroupedThread>();

  threads.forEach((thread) => {
    let position = DEFAULT_THREAD_POSITION_TOP;

    const blockId = thread.metadata.blockId;
    if (blockId) {
      const blockElement = document.querySelector<HTMLElement>(`.block_${blockId}`);
      if (blockElement) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const rect = blockElement.getBoundingClientRect();
        position = rect.top + scrollTop - DEFAULT_THREAD_POSITION_TOP * 2;
      }
    }

    if (!groupedMap.has(position)) {
      groupedMap.set(position, {
        position,
        threads: [],
      });
    }

    groupedMap.get(position)?.threads.push({
      thread,
      isResolved: thread.resolved,
    });
  });

  const groups = Array.from(groupedMap.values());

  groups.sort((a, b) => a.position - b.position);

  return groups;
};
